// Google Tag Manager integration via Stape.io first-party proxy (data.optidev.ai).
// GTM script is loaded via Stape custom loader in index.html (do not modify that snippet).
// This module handles consent mode, dataLayer events, and user context.
//
// Privacy note: userId and workspaceId (internal UUIDs) are included in dataLayer events
// and flow to downstream ad platforms (Google Ads, Meta, TikTok) configured in GTM.

import { resolveConversionValue } from './conversion-values';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
  }
}

const FREE_EMAIL_DOMAINS = new Set([
  // Google
  'gmail.com', 'googlemail.com',
  // Microsoft
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.it', 'hotmail.es',
  'outlook.fr', 'outlook.de', 'outlook.co.uk', 'outlook.es', 'outlook.it',
  'live.co.uk', 'live.fr', 'live.de', 'live.nl',
  // Yahoo
  'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de', 'yahoo.it',
  'yahoo.es', 'yahoo.co.jp', 'yahoo.co.in', 'yahoo.ca', 'yahoo.com.au',
  'yahoo.com.br', 'ymail.com', 'rocketmail.com',
  // Apple
  'icloud.com', 'me.com', 'mac.com',
  // AOL / Verizon
  'aol.com', 'aol.co.uk',
  // Privacy-focused
  'protonmail.com', 'proton.me', 'tutanota.com', 'tutamail.com', 'tuta.io',
  // Other global providers
  'zoho.com', 'mail.com', 'gmx.com', 'gmx.net', 'gmx.de', 'gmx.at',
  'yandex.com', 'yandex.ru', 'ya.ru',
  // Regional (China)
  'qq.com', '163.com', '126.com', 'sina.com', 'sina.cn', '139.com', '188.com',
  // Regional (Europe)
  'web.de', 'freenet.de', 'laposte.net', 'orange.fr', 'sfr.fr', 'free.fr',
  'libero.it', 'virgilio.it', 'wp.pl', 'o2.pl', 'interia.pl',
  // Regional (Asia / Oceania)
  'rediffmail.com', 'naver.com', 'daum.net', 'hanmail.net',
  'bigpond.com', 'optusnet.com.au',
  // Regional (Americas)
  'uol.com.br', 'bol.com.br', 'terra.com.br',
  // ISP / legacy
  'comcast.net', 'sbcglobal.net', 'verizon.net', 'att.net', 'cox.net',
  'charter.net', 'earthlink.net', 'optonline.net', 'btinternet.com',
  'sky.com', 'ntlworld.com',
  // Temp / disposable (common ones that slip through)
  'mailinator.com', 'guerrillamail.com', 'tempmail.com', 'throwaway.email',
]);

let _userContext: {
  userId: string;
  workspaceId: string | undefined;
  isBusinessEmail: boolean;
  user_email: string;
} | null = null;

const IS_PRODUCTION = import.meta.env.VITE_ENV === 'production';

export function initGTM(): void {
  if (!IS_PRODUCTION) return;
  // GTM script is loaded by the Stape custom loader in index.html.
  // Here we only set up consent mode defaults (must be pushed early).
  window.dataLayer = window.dataLayer || [];

  // Google Consent Mode v2 — declare all signals as granted (B2B SaaS, users accept ToS at signup).
  // Required for Enhanced Conversions, remarketing audiences, and EEA data acceptance.
  function gtag(...args: unknown[]): void { window.dataLayer.push(args); }
  gtag('consent', 'default', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
    functionality_storage: 'granted',
    personalization_storage: 'granted',
    security_storage: 'granted',
  });
}

export function setGTMUserContext(userId: string, workspaceId: string | undefined, email: string): void {
  const normalised = email.trim().toLowerCase();
  const domain = normalised.split('@')[1] ?? '';
  _userContext = {
    userId,
    workspaceId,
    isBusinessEmail: domain !== '' && !FREE_EMAIL_DOMAINS.has(domain),
    user_email: normalised,
  };
}

export function clearGTMUserContext(): void {
  _userContext = null;
}

export function pushToDataLayer(event: string, data?: Record<string, unknown>): void {
  if (!IS_PRODUCTION) return;
  window.dataLayer = window.dataLayer || [];

  // Attach the Google Ads conversion value for value-based bidding (tROAS). Only
  // conversion events resolve to a number; everything else stays value-less. Placed
  // before ...data so an explicit caller-supplied `value` still wins if ever needed.
  const conversionValue = resolveConversionValue(event, _userContext?.isBusinessEmail ?? false);

  window.dataLayer.push({
    event,
    ..._userContext,
    ...(conversionValue !== undefined ? { value: conversionValue } : {}),
    ...data,
  });
}

export function trackGTMPageView(path: string): void {
  pushToDataLayer('page_view', { page_path: path });
}
